import Decimal from 'decimal.js';
import { Edge, MarkerType, Node } from '@xyflow/react';
import { DriverType } from '../../../../interfaces/delivery-company/drivers';
import {
  Expense,
  ExpenseMode,
} from '../../../../interfaces/delivery-company/expenses';
import { MerchantType } from '../../../../interfaces/delivery-company/merchants';
import {
  Money,
  add,
  displayMoney,
  moneyToMetric,
} from '../../../../utils/money';
import { AccountingDepartmentNode } from './custom-nodes/accounting-department';
import { DriversNode } from './custom-nodes/drivers';
import { ExpenseNode } from './custom-nodes/expense-node';
import { MerchantsNode } from './custom-nodes/merchants';

export type AugmentedExpense = Expense & { frequency: number };

/**
 * Normalize a list of non-negative Decimal numbers so that their sum equals 1.
 * If the total sum is 0, returns all 0s.
 */
export function normalizeBySum(values: Decimal[]): Decimal[] {
  const total = values.reduce((acc, val) => acc.add(val), new Decimal(0));

  if (total.equals(0)) {
    return values.map(() => new Decimal(0));
  }

  return values.map((value) => value.div(total));
}

export interface CashFlowData {
  drivers: {
    total: Money[];
    profit: Money[];
    details: { driver: DriverType; amount: Money[], profit: Money[]}[];
  };
  merchants: {
    total: Money[];
    pending_total: Money[];
    details: { merchant: MerchantType; amount: Money[], pending_amount: Money[] }[];
  };
  expenses: {
    total: Money[];
    details: AugmentedExpense[];
  };
}

export const labelStyle = {
  fontSize: '16px', // Increase font size here
  fontWeight: '600', // Optional: make it bolder
  fill: '#222', // Optional: label color
};

export const extractDriversAndMerchantsEdges = (c: CashFlowData): Edge[] => {
  return [
    {
      id: 'edge-drivers-accounting',
      source: 'drivers',
      target: 'accounting',
      sourceHandle: 'source',
      targetHandle: 'right-sink',
      labelStyle: labelStyle,
      animated: true,
      style: {
        stroke: '#4CAF50',
      },
      type: 'default',
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: '#222',
      },
      label: displayMoney(c.drivers.total),
    },
    {
      id: 'edge-merchants-accounting',
      target: 'merchants',
      source: 'accounting',
      sourceHandle: 'left-source',
      targetHandle: 'sink',
      animated: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: '#222',
      },
      labelStyle: labelStyle,
      label: displayMoney(add(c.merchants.total, c.merchants.pending_total)),
    },
    {
      id: 'edge-accounting-expenses',
      target: 'expenses',
      source: 'accounting',
      sourceHandle: 'bottom-source',
      targetHandle: 'top-sink',
      animated: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: '#222',
      },
      labelStyle: labelStyle,
      label: displayMoney(c.expenses.total),
    },
  ];
};

export const nodeTypes = {
  driver: DriversNode,
  accounting: AccountingDepartmentNode,
  merchant: MerchantsNode,
  expense: ExpenseNode,
};

export const center = { x: 600, y: 0 };
export const offsetX = 500;
export const offsetY = 200;
export const expenseLabelKey = (e: Expense) =>
  `${e.mode}-${e.category?.id ?? 'no-label'}`;

export const convertExpensesToNodes = (
  expenses: Expense[],
  parentNode: Node
) => {
  let expenseNodes: Node[] = expenses.map((expense, i, arr) => {
    const spacing = 300;
    const totalWidth = (arr.length - 1) * spacing;
    const startX = center.x - totalWidth / 2;

    return {
      id: `expense-${expense.id}`,
      type: 'expense',
      position: {
        x: startX + i * spacing,
        y: parentNode.position.y + 300, // consistent vertical offset below accounting node
      },
      data: { expense },
    };
  });

  const normalizedExpensesAmounts = normalizeBySum(
    expenseNodes.map((node) =>
      moneyToMetric((node.data.expense as Expense).expense)
    )
  );

  expenseNodes = expenseNodes.map((node, i) => {
    return {
      ...node,
      data: { ...node.data, importance: normalizedExpensesAmounts[i] },
    };
  });

  return expenseNodes;
};

export const initialNodes: Node[] = [
  {
    id: 'accounting',
    type: 'accounting',
    position: { x: center.x, y: center.y },
    data: {},
  },
];
